import {
  PortableTextMarkComponentProps,
  PortableTextReactComponents,
  PortableTextTypeComponent
} from '@portabletext/react'
import { useNextSanityImage } from 'next-sanity-image'
import { ReactNode } from 'react'

import { Image as ImageAtom } from 'src/components/atoms/Image'
import { InternalLink } from 'src/components/atoms/Link'
import { TypographyRebrand } from 'src/components/atoms/TypographyRebrand'
import { VideoPlayer } from 'src/components/atoms/VideoPlayer'
import { CtaBandRebrandResult } from 'src/components/layouts/Page/SanityContentPage/types'
import { CmsCtaButtonRebrandProps } from 'src/components/molecules/CmsCtaButtonRebrand'
import { CtaBandSectionRebrand } from 'src/components/molecules/CtaBandSectionRebrand'
import { Text, TextValue } from 'src/components/molecules/sanity/Content'
import { CO2WidgetBlock } from 'src/components/molecules/sanity/blocks/CO2WidgetBlock'
import { CtaBlock } from 'src/components/molecules/sanity/blocks/CtaBlock'
import { InternalRefBlock } from 'src/components/molecules/sanity/blocks/InternalRefBlock'
import { ProjectCarouselRebrand } from 'src/components/organisms/ProjectCarouselRebrand'

import {
  SanityImageAsset,
  SanityReference
} from 'src/config/generated/sanitySchema'
import { sanityClient } from 'src/utils/sanity'

// Rebranded portable-text renderer for blog posts (Figma 63:6297). Text content
// is constrained to 1280px; images span the full 1440px content width.
const TEXT_WIDTH = 'mx-auto w-full px-4 md:px-20 '
const IMAGE_WIDTH = 'mx-auto w-full px-4 md:px-20 '

const PostLink = ({
  children,
  value
}: PortableTextMarkComponentProps<{ _type: 'link'; href: string }>) => (
  <InternalLink
    color='none'
    href={value?.href || ''}
    className='text-forestDeep underline underline-offset-2 hover:opacity-80'
  >
    {children}
  </InternalLink>
)

const PostImage: PortableTextTypeComponent = ({ value }) => {
  const { asset, alt, label } = value as {
    asset: SanityReference<SanityImageAsset>
    alt?: string
    label?: string
  }
  const imageProps = useNextSanityImage(sanityClient, asset)

  return (
    <figure className={IMAGE_WIDTH}>
      <ImageAtom
        {...imageProps}
        alt={alt || ''}
        className='h-auto w-full object-cover'
      />
      {label && (
        <figcaption className='mt-3'>
          <TypographyRebrand variant='paragraph' color='forestDeep'>
            {label}
          </TypographyRebrand>
        </figcaption>
      )}
    </figure>
  )
}

const postBodyComponents: Partial<PortableTextReactComponents> = {
  types: {
    image: PostImage,
    projectCarousel: props => (
      <div className={TEXT_WIDTH}>
        <ProjectCarouselRebrand {...props.value} />
      </div>
    ),
    videoBlock: props => (
      <div className={TEXT_WIDTH}>
        <VideoPlayer {...props.value.video} />
      </div>
    ),
    co2Widget: props => (
      <div className={TEXT_WIDTH}>
        <CO2WidgetBlock {...props} />
      </div>
    ),
    ctaBlock: props => (
      <div className={TEXT_WIDTH}>
        <CtaBlock {...props} />
      </div>
    ),
    actionBlockRebrand: ({ value }) => {
      const { heading, subheading, color, link } = value as {
        heading?: string
        subheading?: string
        color?: CtaBandRebrandResult['color']
        link?: CmsCtaButtonRebrandProps
      }
      return (
        <div className={TEXT_WIDTH}>
          <CtaBandSectionRebrand
            _type='ctaBandRebrand'
            heading={heading}
            subheading={subheading}
            color={color ?? 'limeBright'}
            buttons={link ? [link] : []}
          />
        </div>
      )
    }
  },
  marks: {
    link: PostLink,
    internalRefBlock: InternalRefBlock,
    left: ({ children }: { children: ReactNode }) => (
      <span className='block text-left'>{children}</span>
    ),
    center: ({ children }: { children: ReactNode }) => (
      <span className='block text-center'>{children}</span>
    ),
    right: ({ children }: { children: ReactNode }) => (
      <span className='block text-right'>{children}</span>
    )
  },
  block: {
    normal: ({ children }) => (
      <div className={TEXT_WIDTH}>
        <TypographyRebrand variant='body' color='forestDeep'>
          {children}
        </TypographyRebrand>
      </div>
    ),
    h1: ({ children }) => (
      <div className={TEXT_WIDTH}>
        <TypographyRebrand variant='h2' color='forestDeep'>
          <strong>{children}</strong>
        </TypographyRebrand>
      </div>
    ),
    h2: ({ children }) => (
      <div className={TEXT_WIDTH}>
        <TypographyRebrand variant='h3' color='forestDeep'>
          {children}
        </TypographyRebrand>
      </div>
    ),
    h3: ({ children }) => (
      <div className={TEXT_WIDTH}>
        <TypographyRebrand variant='h4' color='forestDeep'>
          {children}
        </TypographyRebrand>
      </div>
    ),
    h4: ({ children }) => (
      <div className={TEXT_WIDTH}>
        <TypographyRebrand variant='h5' color='forestDeep'>
          {children}
        </TypographyRebrand>
      </div>
    ),
    blockquote: ({ children }) => (
      <div className={TEXT_WIDTH}>
        <blockquote className='my-8 border-l-4 border-limeBright pl-6'>
          <TypographyRebrand variant='introduction' color='forestDeep'>
            {children}
          </TypographyRebrand>
        </blockquote>
      </div>
    ),
    callout: ({ children }) => (
      <div className={TEXT_WIDTH}>
        <div className='my-6 bg-limeBright/20 p-5 pt-7 lg:p-6 lg:pt-8'>
          <TypographyRebrand variant='paragraph' color='forestDeep'>
            {children}
          </TypographyRebrand>
        </div>
      </div>
    )
  },
  list: {
    bullet: ({ children }) => (
      <div className={TEXT_WIDTH}>
        <ul className='typography-rebrand-body my-6 list-disc space-y-3 pl-6 text-forestDeep marker:text-limeBright'>
          {children}
        </ul>
      </div>
    ),
    number: ({ children }) => (
      <div className={TEXT_WIDTH}>
        <ol className='typography-rebrand-body my-6 list-decimal space-y-3 pl-6 text-forestDeep marker:font-semibold marker:text-forestDeep'>
          {children}
        </ol>
      </div>
    )
  },
  listItem: {
    bullet: ({ children }) => <li className='pl-1'>{children}</li>,
    number: ({ children }) => <li className='pl-1'>{children}</li>
  }
}

export const PostBody = ({ body }: { body: TextValue }) => (
  <Text components={postBodyComponents} value={body} />
)

export default PostBody
